import { ChangeEvent, useState } from 'react';

import CropperDefault, { Area } from 'react-easy-crop';
import toast from 'react-hot-toast';
import { observer } from 'mobx-react-lite';
import { faRotate } from '@fortawesome/pro-solid-svg-icons/faRotate';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Slider } from 'us-react-components/dist/Slider/Slider';
import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { UserPicture, UserPictureService } from 'us-web-services';

import { Button, ButtonSize, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';
import { useModal } from '../modalContext';
import { getCroppedImg } from 'us-react-components/dist/utils/cropper';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Cropper = CropperDefault as unknown as (props: any) => JSX.Element;

export const CropperModalContent = observer((): JSX.Element => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [croppedPhotoURL, setCroppedPhotoURL] = useState<string>();
  const [saving, setSaving] = useState(false);
  const modal = useModal();
  const {
    stores: { user, sitter },
  } = useDependencies();
  const { t } = useSharedTranslation(namespaces.caregiver);

  const handleSliderChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setZoom(Number(event.target.value));
  };

  const closeModal = (): void => {
    modal?.setModalName(undefined);
  };

  const rotate = (): void => {
    setRotation((rotation + 90) % 360);
  };

  const onCropComplete = async (_croppedArea: Area, pixelCrop: Area): Promise<void> => {
    if (!pixelCrop.height || !pixelCrop.width) return;
    try {
      const cropped = await getCroppedImg(
        user.temporaryPhoto,
        pixelCrop,
        rotation,
      );
      setCroppedPhotoURL(cropped!);
    } catch {
      toast.error(t('pages.sitterPhoto.uploadFailed'));
    }
  };

  const onSave = async (): Promise<void> => {
    if (!croppedPhotoURL || !user.user.id || saving) return;
    setSaving(true);
    try {
      const response = await fetch(croppedPhotoURL);
      const blobData = await response.blob();
      const formData = new FormData();
      formData.append('file', blobData);
      formData.append('userId', user.user.id.toString());

      await UserPictureService.create(formData as UserPicture);
      await user.refreshUser();
      await sitter.refreshSitter?.();
      closeModal();
    } catch {
      toast.error(t('pages.sitterPhoto.uploadFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='h-[450px] xl:h-[500px]'>
      <Button aria-label={t('common.cancel')} className='float-right' onClick={closeModal}>
        <FontAwesomeIcon icon={faXmark} />
      </Button>
      <div className='w-full h-[70%] flex justify-center items-center'>
        <Cropper
          classes={{
            containerClassName: 'w-[80%] h-[50%] mx-auto mt-16 xl:mt-20',
          }}
          image={user.temporaryPhoto}
          crop={crop}
          rotation={rotation}
          zoom={zoom}
          aspect={1}
          onCropChange={setCrop}
          onRotationChange={setRotation}
          onCropComplete={onCropComplete}
          onZoomChange={setZoom}
          cropShape='round'
        />
      </div>
      <div className='w-full h-[25%] flex flex-col'>
        <Slider
          label={t('pages.sitterPhoto.zoom')}
          value={zoom}
          onChange={handleSliderChange}
          min={1}
          max={3}
        />
        <div className='flex justify-center pt-6 xl:pt-10'>
          <Button
            buttonType={ButtonType.primary}
            buttonSize={ButtonSize.medium}
            onClick={onSave}
            disabled={saving || !croppedPhotoURL}
          >
            {saving ? t('pages.sitterPhoto.uploading') : t('common.save')}
          </Button>
          <Button
            buttonType={ButtonType.tertiary}
            className='ml-8'
            onClick={rotate}
            aria-label={t('pages.sitterPhoto.rotate')}
          >
            <FontAwesomeIcon size='xl' icon={faRotate} />
          </Button>
        </div>
      </div>
    </div>
  );
});
